import { useLazyQuery, useMutation } from '@apollo/client';
import { flatten } from '../dataParsing/searchItemFormatting';
import { RESOURCE_QUERY } from '../../../gql/queries';
import { CREATE_RESOURCE_MUTATION, DELETE_RESOURCE_MUTATION, PATCH_RESOURCE_MUTATION } from '../../../gql/mutations/RESOURCE_MUTATION';
import { CREATE_LOCALIZATION_MUTATION } from '../../../gql/mutations/CREATE_LOCALIZATION_MUTATION';
import { PATCH_LOCALIZATION_MUTATION } from '../../../gql/mutations/PATCH_LOCALIZATION_MUTATION';
import { DELETE_LOCALIZATION_MUTATION } from '../../../gql/mutations/DELETE_LOCALIZATION_MUTATION';
import { CREATE_RESOURCE_URL_MUTATION } from '../../../gql/mutations/RESOURCE_URL_MUTATIONS';
import { useUploadLXPFile } from '../../../hooks';

export const RESOURCE_TYPE = {
    FILE: 'file',
    URL: 'url'
};

/**
 * Flattened localization shape used by the form and the localization modal
 * @param {Object} l localization node, already run through flatten()
 */
function formatLocalizationItem( l ) {
    const file = l.resourceFile?.genericFile ?? l.resourceFile?.lxpFile ?? null;

    return {
        id: l.id,
        locale: l.localeCode,
        isDefault: l.isDefault,
        name: l.name,
        description: l.description,
        resourceFileId: l.resourceFileId,
        resourceUrlId: l.resourceUrlId,
        url: l.resourceUrl?.url,
        // asset is what the upload form item is controlled by. `file` is only set once the
        // user picks a new one, which is how we know whether the asset needs re-uploading
        asset: file ? { name: file.filename ?? file.name, url: file.url ?? file.s3Url } : null,
    };
}

function formatResourceQueryItem( r ) {
    return {
        ...r,
        id: r.id,
        // useCreateEditGeneric reads data[ idName ] after a create to build the edit url
        resourceId: r.id,
        localizations: ( r.resourceLocalizations ?? [] ).map( formatLocalizationItem ),
        originalData: r,
    };
}

/**
 * Attaches a localization's file or url to the resource.
 * @param {Object} localization form values of the localization being saved
 * @param {Object} existing the localization as it exists on the server, when editing
 * @returns { resourceFileId, resourceUrlId } to hand to the localization mutation
 */
const useResourceAssetUpload = () => {

    const { uploadFile } = useUploadLXPFile();
    const [ createResourceUrl ] = useMutation( CREATE_RESOURCE_URL_MUTATION );

    async function uploadAsset( { resourceId, type, localization, existing } ) {

        if ( type === RESOURCE_TYPE.URL ) {

            if ( existing && localization.url === existing.url ) {
                return { resourceUrlId: existing.resourceUrlId ?? undefined };
            }

            const result = await createResourceUrl( { variables: { input: { resourceId, url: localization.url } } } );
            const resourceUrlId = result?.data?.createResourceUrl?.id;

            if ( !resourceUrlId ) {
                throw new Error( 'Failed to save resource URL' );
            }

            return { resourceUrlId };
        }

        const file = localization.asset?.file;

        if ( !file ) {
            return { resourceFileId: existing?.resourceFileId ?? undefined };
        }

        const resourceFileId = await uploadFile( { file, resourceId } );

        if ( !resourceFileId || resourceFileId.error ) {
            throw new Error( 'Failed to upload resource file' );
        }

        return { resourceFileId };
    }

    return { uploadAsset };
};

export const useResourceQuery = () => {

    const [ fetchResource ] = useLazyQuery( RESOURCE_QUERY, { fetchPolicy: 'no-cache' } );

    async function fetch( { id } ) {
        const variables = {
            resourceID: id
        };

        const result = await fetchResource( { variables } );

        const resource = flatten( result.data?.resource );

        if ( !resource ) {
            throw new Error( 'Could not find resource' );
        }

        const data = formatResourceQueryItem( resource );

        return { data };
    }

    return [ fetch ];
};

/**
 * A resource is not usable until it has a default localization holding its file or url,
 * so creation is the whole chain rather than a single mutation.
 */
export const useCreateResourceMutation = () => {

    const [ createResource ] = useMutation( CREATE_RESOURCE_MUTATION );
    const [ createLocalization ] = useMutation( CREATE_LOCALIZATION_MUTATION );
    const [ patchResource ] = useMutation( PATCH_RESOURCE_MUTATION );
    const { uploadAsset } = useResourceAssetUpload();
    const [ fetch ] = useResourceQuery();

    async function create( resource ) {

        const { type, defaultLocalization: localization } = resource;

        const result = await createResource( { variables: { input: {
            isPublished: resource.isPublished,
            isFeatured: resource.isFeatured,
            type,
            name: localization?.name,
            description: localization?.description,
        } } } );

        const resourceId = result?.data?.result?.id;

        if ( !resourceId ) {
            throw new Error( 'Failed to create resource' );
        }

        const { resourceFileId, resourceUrlId } = await uploadAsset( { resourceId, type, localization } );

        await createLocalization( { variables: { input: {
            isDefault: true,
            localeCode: localization.locale,
            resourceId,
            name: localization.name,
            description: localization.description,
            resourceFileId,
            resourceUrlId,
        } } } );

        // CreateResourceInput has no image, so a thumbnail picked while creating is attached
        // with a follow up patch
        if ( resource.image ) {
            await patchResource( { variables: { input: {
                resourceId,
                name: localization?.name,
                description: localization?.description,
                isPublished: resource.isPublished,
                isFeatured: resource.isFeatured,
                type,
                image: resource.image,
            } } } );
        }

        return await fetch( { id: resourceId } );
    }

    return [ create ];
};

/**
 * patchResource only echoes back a handful of fields, so refetch instead of trusting the
 * payload. The refetch also picks up the tag and segmentation syncs done in beforeSave.
 */
export const usePatchResourceMutation = () => {

    const [ patchResource ] = useMutation( PATCH_RESOURCE_MUTATION );
    const [ fetch ] = useResourceQuery();

    async function patch( resource ) {

        const result = await patchResource( { variables: { input: {
            resourceId: resource.resourceId,
            name: resource.name,
            description: resource.description,
            isPublished: resource.isPublished,
            isFeatured: resource.isFeatured,
            type: resource.type,
            image: resource.image ?? null,
        } } } );

        if ( !result?.data?.result?.id ) {
            throw new Error( 'Failed to save resource' );
        }

        return await fetch( { id: resource.resourceId } );
    }

    return [ patch ];
};

export const useDeleteResourceMutation = () => {

    const [ deleteResourceMutation ] = useMutation( DELETE_RESOURCE_MUTATION );

    async function deleteResource( resourceId ) {
        const variables = {
            input: {
                resourceId
            }
        };

        await deleteResourceMutation( { variables } );

        return { success: true };
    }

    return [ deleteResource ];
};

/**
 * Localizations are not part of PatchResourceInput - they are saved on their own as soon as
 * the localization modal is submitted.
 */
export const useResourceLocalizationMutations = () => {

    const [ createLocalization ] = useMutation( CREATE_LOCALIZATION_MUTATION );
    const [ patchLocalization ] = useMutation( PATCH_LOCALIZATION_MUTATION );
    const [ deleteLocalization ] = useMutation( DELETE_LOCALIZATION_MUTATION );
    const { uploadAsset } = useResourceAssetUpload();

    async function create( { resourceId, type, localization } ) {

        const { resourceFileId, resourceUrlId } = await uploadAsset( { resourceId, type, localization } );

        await createLocalization( { variables: { input: {
            isDefault: false,
            localeCode: localization.locale,
            resourceId,
            name: localization.name,
            description: localization.description,
            resourceFileId,
            resourceUrlId,
        } } } );

        return { success: true };
    }

    async function patch( { resourceId, type, localization, existing } ) {

        const { resourceFileId, resourceUrlId } = await uploadAsset( { resourceId, type, localization, existing } );

        await patchLocalization( { variables: { input: {
            isDefault: existing.isDefault,
            localeCode: existing.locale,
            resourceId,
            resourceLocalizationId: existing.id,
            name: localization.name,
            description: localization.description,
            resourceFileId,
            resourceUrlId,
        } } } );

        return { success: true };
    }

    async function remove( { resourceId, localizationId } ) {

        await deleteLocalization( { variables: { input: { resourceId, resourceLocalizationId: localizationId } } } );

        return { success: true };
    }

    return { create, patch, remove };
};
